import type { ComponentProps } from 'react';
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { ContentLink, ContentOfferTeaserSlice } from 'graphql-ts-types';
import {
  backgroundColor,
  border,
  borderColor,
  borderStyle,
  borderWidth,
  boxShadow,
  color,
  display,
  gridColumn,
  height,
  position,
  right,
  space,
  width,
} from 'styled-system';

import { Button, ButtonBase } from 'common/components/Button';
import { Flex } from 'common/components/Flex';
import { ResponsiveLazyImage } from 'common/components/LazyImage';
import type { ResponsiveLazyImageProps } from 'common/components/LazyImage/types';
import { Link } from 'common/components/Link';
import { HeadlineH4, HeadlineH8, LinkRegularLR2, SubtitleST3 } from 'common/components/Typography';
import { useOpenDrawer } from 'common/hooks/drawer';
import { usePromoEvents } from 'common/hooks/dynamic-yield';
import { useIsObserved } from 'common/hooks/intersection-observer';
import { textDecoration, themeBottom, whiteSpace } from 'common/theme/system-utilities';
import type { PageAppTrackerProps } from 'common/tracking/getTrackerProps';
import { Stoerer } from './Stoerer';

interface OfferTeaserItemProps extends Omit<
  ContentOfferTeaserSlice,
  'contentId' | 'image' | 'enhancedImage' | 'ctaButtonLink' | 'name'
> {
  image: Nullable<ResponsiveLazyImageProps>;
  enhancedImage: Nullable<ResponsiveLazyImageProps>;
  ctaButtonLink: Pick<ContentLink, 'fullUrl' | 'target'>;
  tracking: PageAppTrackerProps;
  index: number;
  isEnhanced: boolean;
}

const IMAGE_HEIGHT = ['168px', null, '176px'];
const ENHANCED_IMAGE_HEIGHT = ['168px', '214px', '252px', '176px'];
const ENHANCED_CONTENT = {
  alignItems: ['center', null, null, 'flex-start'],
  flexDirection: ['column', 'row', null, 'column'],
  justifyContent: ['center', 'space-between', null, 'center'],
};

const UnstyledTeaserItem = styled(Flex)<any>`
  ${backgroundColor};
  ${gridColumn};
`;

const TeaserItem = (props: ComponentProps<typeof UnstyledTeaserItem>) => (
  <UnstyledTeaserItem backgroundColor="sys.neutral.background.default" {...props} />
);

const UnstyledStyledLink = styled(Link)<any>`
  ${borderStyle};
  ${borderWidth};
  ${borderColor};
  ${border}
  ${boxShadow};
  ${width};
`;

const StyledLink = (props: ComponentProps<typeof UnstyledStyledLink>) => (
  <UnstyledStyledLink
    width={1}
    borderStyle={[null, 'solid']}
    borderWidth={[null, 'small']}
    borderColor={[null, 'sys.neutral.border.inactive']}
    boxShadow={['0 2px 6px 0 rgba(192, 192, 192, 0.5)', 'none']}
    {...props}
  />
);

const UnstyledImageContainer = styled.div<any>`
  ${position};
`;

const ImageContainer = (props: ComponentProps<typeof UnstyledImageContainer>) => (
  <UnstyledImageContainer position="relative" {...props} />
);

const UnstyledImage = styled(ResponsiveLazyImage)<any>`
  ${width};
  ${height};
  ${display};
  object-fit: cover;
`;

const Image = (props: ComponentProps<typeof UnstyledImage>) => (
  <UnstyledImage display="block" width={1} height={IMAGE_HEIGHT} {...props} />
);

const UnstyledContent = styled(Flex)<any>``;

const Content = (props: ComponentProps<typeof UnstyledContent>) => (
  <UnstyledContent
    px={[null, 'sp_24', null, 'sp_32']}
    py={['sp_16', 'sp_24', 'sp_20']}
    flexDirection="column"
    justifyContent="center"
    alignItems={['center', 'flex-start']}
    {...props}
  />
);

const UnstyledTextContainer = styled(Flex)<any>``;

const TextContainer = (props: ComponentProps<typeof UnstyledTextContainer>) => (
  <UnstyledTextContainer flexDirection="column" alignItems={['center', 'normal']} {...props} />
);

const UnstyledCtaButton = styled(Button)<any>`
  ${space};
  ${whiteSpace};
`;

const CtaButton = (props: ComponentProps<typeof UnstyledCtaButton>) => (
  <UnstyledCtaButton
    kind="secondary"
    mt="sp_12"
    px="sp_24"
    py="sp_8"
    whiteSpace="nowrap"
    {...props}
  />
);

const UnstyledLegalDisclaimerButton = styled(ButtonBase)<any>`
  ${color};
  ${position};
  ${right};
  ${themeBottom};
  ${textDecoration};
`;

const LegalDisclaimerButton = (props: ComponentProps<typeof UnstyledLegalDisclaimerButton>) => (
  <UnstyledLegalDisclaimerButton
    position="absolute"
    right={0}
    themeBottom={['sp_4', null, null, 'sp_8']}
    px="sp_8"
    textDecoration="underline"
    {...props}
  />
);

// eslint-disable-next-line complexity
const OfferTeaserItem = ({
  title,
  subtitle,
  image,
  enhancedImage,
  ctaButtonLabel,
  ctaButtonLink,
  stoererLabel,
  stoererBorderColor,
  legalDisclaimerLinkLabel,
  legalDisclaimerLinkColor,
  legalDisclaimerContent,
  tracking,
  index,
  isEnhanced,
}: OfferTeaserItemProps) => {
  const teaserImage = isEnhanced && enhancedImage ? enhancedImage : image;
  const withDisclaimer = legalDisclaimerLinkLabel && legalDisclaimerContent;
  const openLegalDisclaimerDrawer = useOpenDrawer(
    { html: legalDisclaimerContent },
    {
      preventDefault: true,
      stopPropagation: true,
    }
  );
  const [ref, isObserved] = useIsObserved();
  const { onPromoView, onPromoClick } = usePromoEvents({ ...tracking, position: index });

  useEffect(() => {
    if (isObserved) {
      onPromoView();
    }
  }, [isObserved]);

  return (
    <TeaserItem ref={ref} {...(isEnhanced ? { gridColumn: [1, 'span 2', null, 3] } : {})}>
      <StyledLink data-prefetch={true} href={ctaButtonLink.fullUrl} onClick={onPromoClick}>
        <ImageContainer>
          {stoererLabel && (
            <Stoerer
              label={stoererLabel}
              themeTop={[0, 'sp_16', 'sp_24']}
              translate3d={['-50%, 0, 0', '0, 0, 0']}
              {...(stoererBorderColor ? { borderColor: stoererBorderColor } : {})}
            />
          )}
          <Image
            alt={teaserImage?.alt}
            {...teaserImage}
            {...(isEnhanced ? { height: ENHANCED_IMAGE_HEIGHT } : {})}
          />
          {withDisclaimer && (
            <LegalDisclaimerButton
              onClick={legalDisclaimerContent ? openLegalDisclaimerDrawer : null}
              {...(legalDisclaimerLinkColor
                ? {
                    color: legalDisclaimerLinkColor,
                  }
                : {})}
            >
              <LinkRegularLR2 as="span" color="inherit">
                *{legalDisclaimerLinkLabel}
              </LinkRegularLR2>
            </LegalDisclaimerButton>
          )}
        </ImageContainer>
        <Content {...(isEnhanced ? ENHANCED_CONTENT : {})}>
          <TextContainer
            {...(isEnhanced
              ? {
                  flexDirection: ['column', null, 'row', 'column'],
                  alignItems: ['center', 'normal', 'baseline', 'normal'],
                }
              : {})}
          >
            <HeadlineH4 color="sys.neutral.text.default" textAlign={['center', 'left']}>
              {title}
            </HeadlineH4>
            {subtitle && (
              <SubtitleST3
                textAlign={['center', 'left']}
                {...(isEnhanced ? { ml: [null, null, 'sp_8', 'sp_0'] } : {})}
              >
                {subtitle}
              </SubtitleST3>
            )}
          </TextContainer>
          <CtaButton {...(isEnhanced ? { mt: ['sp_12', null, 'sp_0', 'sp_12'] } : {})}>
            <HeadlineH8 as="span" color="sys.neutral.text.default">
              {ctaButtonLabel}
            </HeadlineH8>
          </CtaButton>
        </Content>
      </StyledLink>
    </TeaserItem>
  );
};

export default OfferTeaserItem;
